import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Spinner,
  FormFeedback,
} from "reactstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import CommonForm from "../../../../Components/Common/CommonForm";
import styles from "../css/addstudy.module.scss";
import { useStudyDetailsService } from "hooks/useCommonQuery";
import { useMasterPeriodService } from "modules/Library/Period/api/usePeriodService";
import { useNavigate } from "react-router-dom";
import SimpleBar from "simplebar-react";
import { queryClient } from "api/queryClient";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { useAliquotQuery } from "modules/Library/Aliquote/api/useAliquotService";
import { StudyFormValues } from "../helpers/study.types";
import {
  buildStudyChecklistsPayload,
  dynamicFields,
  fields,
  getCurrentTabFields,
  normalizeFormValues,
  normalizeStudyForm,
  periodColumns,
  refDrugFieldNames,
  tabConfig,
  testDrugFieldNames,
  totalStudyDurationText,
  visitformfields,
} from "../helpers/studyconstant";
import { parseLocalDate, showToastMessage } from "common/service/CommonService";
import CRFScreeningModal from "modules/Volunteer/AssigningStudyModule/StudyScreening/components/CRFScreeningModal";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { validations } from "validations/FormValidations";
import ChecklistAssignmentStep from "../components/ChecklistAssignmentStep";
import CRFAssignmentStep from "../components/CRFAssignmentStep";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import { buildStudyCRFsPayload } from "../helpers/studyconstant";

interface AddStudyPageProps {
  onClose?: () => void;
  onCreated?: () => void;
}

const AddStudyForm: React.FC<AddStudyPageProps> = ({ onClose, onCreated }) => {
  const navigate = useNavigate();
  document.title = "Add Study | ZENTIXS VISTA";

  const [activeTab, setActiveTab] = useState<number>(1);
  const [samplingpoints, setSamplingPoints] = useState<any[]>([]);
  const tabItemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [progressHeight, setProgressHeight] = useState(0);
  const [maxUnlockedTab, setMaxUnlockedTab] = useState<number>(1);
  const [getOldNumberOfPeriod, setOldNumberOfPeriod] = useState<number | null>(
    null,
  );
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    trigger,
    clearErrors,
    unregister,
  } = useForm<StudyFormValues>({
    defaultValues: {
      checklistassignmentmode: "common",
    },
    mode: "onBlur",
  });



  // Fetch CRFs at main form level and pass query to CRFAssignmentStep
  const { AssignCRFSelectAllQuery: studyCRFAssignmentQuery } = useCRFService({
    moduleid: null,
    modulename: null,
    categorynames: ["study"],
    pageNumber: 1,
    pageSize: 1000,
    enabledSelectAll: false,
    enabledCategorySelectAll: false,
    enabledAssignCRFSelectAll: true,
  });

  const {
    control: visitControl,
    handleSubmit: handleVisitSubmit,
    setValue: setVisitValue,
    getValues: getVisitValues,
    reset: resetVisit,
    formState: { errors: visitErrors },
    trigger: triggerVisit,
    clearErrors: clearVisitErrors,
    unregister: unregisterVisit,
  } = useForm<any>({
    defaultValues: {
      visitname: "",
      visittype: "",
      visitcategory: "",
      ambulatorytotalsamplingpoints: "",
      samplingpoints: [],
      deviation: "",
      visitdescription: "",
    },
    mode: "onSubmit",
  });

  const watchedAmbulatoryTotalSamplingPoints = useWatch({
    control: visitControl,
    name: "ambulatorytotalsamplingpoints",
  });

  const [ambulatorySamplingPointsUI, setAmbulatorySamplingPointsUI] = useState<any[]>([]);

  useEffect(() => {
    if (!watchedAmbulatoryTotalSamplingPoints || Number(watchedAmbulatoryTotalSamplingPoints) <= 0) {
      setAmbulatorySamplingPointsUI([]);
      return;
    }

    const count = Number(watchedAmbulatoryTotalSamplingPoints) || 0;
    setAmbulatorySamplingPointsUI(Array.from({ length: count }, (_, i) => i + 1));

    const currentPoints = getVisitValues("samplingpoints") || [];
    if (Array.isArray(currentPoints) && currentPoints.length > count) {
      for (let i = count; i < currentPoints.length; i++) {
        unregisterVisit(`samplingpoints.${i}` as any);
      }
      setVisitValue("samplingpoints", currentPoints.slice(0, count));
    }
  }, [watchedAmbulatoryTotalSamplingPoints]);

  const ambulatoryFormFields = useMemo(() => {
    return getCurrentTabFields(12);
  }, []);

  const watchedTimeline = watch();
  const numPeriods = watch("numberofperiods");

  const { InsertMutation } = useStudyDetailsService(null);

  // Fetch checklist master data ONCE at form level so it's not re-fetched on tab switch
  const { SelectAllQuery: checklistMasterQuery } = useCheckListMasterService({
    checklistcategory: "study",
    ispagination: false,
    typeEnabled: false,
  });
  const rawChecklistData: any[] = (checklistMasterQuery?.data as any)?.data || [];

  // Fetch all master period records for display sequence dropdown
  const { SelectAllQuery: masterPeriodSelectAll } = useMasterPeriodService(
    "",
    undefined,
    1,
    1000,
  );

  const masterPeriodData: any[] =
    (masterPeriodSelectAll as any)?.data?.data ?? [];
  const displaySequenceOptions = masterPeriodData.map((item: any) => ({
    label: String(item.period_number),
    value: String(item.period_number),
  }));

  // Fetch all master period records for display sequence dropdown
  const { SelectAllQuery: masterAliquoteSelectAll } = useAliquotQuery(
    "",
    1,
    1000,
  );

  const masterAliquoteData: any[] =
    (masterAliquoteSelectAll as any)?.data?.data ?? [];
  const displayAliquoteSequenceOptions = masterAliquoteData.map(
    (item: any) => ({
      label: String(item.aliquot_number),
      value: String(item.aliquot_number),
    }),
  );

  // Determine if a tab is completed (all tabs before the current active tab)
  const isTabCompleted = (tabId: number) => tabId < activeTab;
  const isTabActive = (tabId: number) => tabId === activeTab;

  useEffect(() => {
    const activeEl = tabItemRefs.current[activeTab];
    const container = sidebarRef.current;

    if (!activeEl || !container) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();

    const scrollOffset =
      activeRect.top -
      containerRect.top +
      container.scrollTop -
      containerRect.height / 2 +
      activeEl.offsetHeight / 2;

    container.scrollTo({
      top: scrollOffset,
      behavior: "smooth",
    });

    // ✅ FIX START
    const progressStartOffset = 40;
    const tabOffsetTop = activeEl.offsetTop;

    const iconCenterOffset = activeEl.offsetHeight / 2;

    let height = tabOffsetTop + iconCenterOffset - progressStartOffset;

    setProgressHeight(Math.max(0, height));
  }, [activeTab]);

  const agemin = watch("agemin");
  const agemax = watch("agemax");
  const bmimin = watch("bmimin");
  const bmimax = watch("bmimax");
  const testdrugname = watch("testdrugname");
  const testmanufacturingdate = watch("testmanufacturingdate");
  const refdrugname = watch("refdrugname");
  const refmanufacturingdate = watch("refmanufacturingdate");
  const testDrugRequired = !!String(testdrugname || "").trim();
  const refDrugRequired = !!String(refdrugname || "").trim();

  useEffect(() => {
    if (agemin === null || agemin === undefined || agemin === "") {
      setValue("agemax", "");
      clearErrors("agemax");
      return;
    }
  }, [agemin, agemax]);

  useEffect(() => {
    if (bmimin === null || bmimin === undefined || bmimin === "") {
      setValue("bmimax", "");
      clearErrors("bmimax");
      return;
    }
  }, [bmimin, bmimax]);

  useEffect(() => {
    if (!testDrugRequired) {
      testDrugFieldNames.forEach((fieldName) => {
        unregister(fieldName as any, { keepValue: true });
        clearErrors(fieldName as any);
      });
    }
  }, [testDrugRequired]);

  useEffect(() => {
    if (!refDrugRequired) {
      refDrugFieldNames.forEach((fieldName) => {
        unregister(fieldName as any, { keepValue: true });
        clearErrors(fieldName as any);
      });
    }
  }, [refDrugRequired]);

  const totalSamplingPoints = watch("totalsamplingpoints");
  const samplingErrors = errors.samplingpoints as any;

  // useEffect(() => {
  //   if (!totalSamplingPoints) return;
  //   setSamplingPoints(Array.from({ length: totalSamplingPoints }, () => ""));
  //   clearErrors("samplingpoints");
  // }, [totalSamplingPoints]);

  useEffect(() => {
    if (totalSamplingPoints && String(totalSamplingPoints).length > 2) {
      const sliced = Number(String(totalSamplingPoints).slice(0, 2));
      setValue("totalsamplingpoints", sliced as any, { shouldValidate: true });
      return;
    }

    if (!totalSamplingPoints) {
      const currentFields = getValues("samplingpoints") || [];
      currentFields.forEach((_: any, i: number) =>
        unregister(`samplingpoints.${i}` as any),
      );
      setValue("samplingpoints" as any, []);
      setSamplingPoints([]);
      clearErrors("samplingpoints");
      return;
    }

    const currentFields = getValues("samplingpoints") || [];

    // Unregister any indices beyond the new count so RHF drops them from state
    if (currentFields.length > totalSamplingPoints) {
      for (let i = totalSamplingPoints; i < currentFields.length; i++) {
        unregister(`samplingpoints.${i}` as any);
      }
    }

    const newArray = Array.from(
      { length: totalSamplingPoints },
      (_, i) => currentFields[i] || "",
    );

    setValue("samplingpoints" as any, newArray);
    setSamplingPoints(newArray);
    clearErrors("samplingpoints");
  }, [totalSamplingPoints]);

  useEffect(() => {
    if (numPeriods && getOldNumberOfPeriod) return;
    for (let i = 1; i <= (getOldNumberOfPeriod ?? 0); i++) {
      setValue(`period${i}startdate` as any, "");
      setValue(`period${i}checkindate` as any, "");
      setValue(`period${i}checkoutdate` as any, "");
      trigger(`period${i}startdate`);
      trigger(`period${i}checkindate`);
      trigger(`period${i}checkoutdate`);
    }
    setOldNumberOfPeriod(numPeriods);
  }, [numPeriods]);

  const washoutDaysVal = watch("washoutdays");

  useEffect(() => {
    if (!numPeriods) return;
    const washout = Number(washoutDaysVal) || 0;

    for (let i = 1; i <= numPeriods; i++) {
      const currentStart = watchedTimeline[`period${i}startdate` as keyof StudyFormValues];
      const currentCheckin = watchedTimeline[`period${i}checkindate` as keyof StudyFormValues];
      const currentCheckout = watchedTimeline[`period${i}checkoutdate` as keyof StudyFormValues];

      // 1. If Start Date is empty, checkin and checkout must be empty
      if (!currentStart) {
        if (currentCheckin) {
          setValue(`period${i}checkindate` as any, "");
          trigger(`period${i}checkindate` as any);
        }
        if (currentCheckout) {
          setValue(`period${i}checkoutdate` as any, "");
          trigger(`period${i}checkoutdate` as any);
        }
      }

      // 2. If Check-in is empty, checkout must be empty
      if (!currentCheckin && currentCheckout) {
        setValue(`period${i}checkoutdate` as any, "");
        trigger(`period${i}checkoutdate` as any);
      }

      // 3. For period i > 1: check previous checkout date and handle autofill or clear
      if (i > 1) {
        const prevCheckoutVal = watchedTimeline[`period${i - 1}checkoutdate` as keyof StudyFormValues];

        if (prevCheckoutVal) {
          // Autofill Start Date logic
          const minDate = parseLocalDate(prevCheckoutVal as string);
          if (minDate && !isNaN(minDate.getTime())) {
            minDate.setDate(minDate.getDate() + washout + 1);
            const yyyy = minDate.getFullYear();
            const mm = String(minDate.getMonth() + 1).padStart(2, "0");
            const dd = String(minDate.getDate()).padStart(2, "0");
            const formattedMinDate = `${yyyy}-${mm}-${dd}`;

            const currentStartParsed = parseLocalDate(currentStart);
            if (!currentStart || (currentStartParsed && currentStartParsed.getTime() < minDate.getTime())) {
              setValue(`period${i}startdate` as any, formattedMinDate);
              trigger(`period${i}startdate` as any);
            }
          }
        } else {
          // Previous checkout is cleared/empty: clear current period's Start, Check-in, and Check-out
          if (currentStart) {
            setValue(`period${i}startdate` as any, "");
            trigger(`period${i}startdate` as any);
          }
          if (currentCheckin) {
            setValue(`period${i}checkindate` as any, "");
            trigger(`period${i}checkindate` as any);
          }
          if (currentCheckout) {
            setValue(`period${i}checkoutdate` as any, "");
            trigger(`period${i}checkoutdate` as any);
          }
        }
      }
    }
  }, [washoutDaysVal, numPeriods, watchedTimeline]);

  // Total Sampling Points UI end

  const handleTabClick = async (targetTabId: number) => {
    if (targetTabId === activeTab) return;

    if (targetTabId < activeTab) {
      setActiveTab(targetTabId);
      return;
    }

    // Progressive unlocking: only allow if already unlocked or if next immediate tab
    if (targetTabId > maxUnlockedTab && targetTabId !== activeTab + 1) {
      return;
    }

    let currentFields: string[] = getCurrentTabFields(activeTab).map(
      (f) => f.name as string,
    );

    if (activeTab === 9 && numPeriods) {
      const periodFields = Array.from({ length: numPeriods }, (_, i) => [
        `period${i + 1}startdate`,
        `period${i + 1}checkindate`,
        `period${i + 1}checkoutdate`,
      ]).flat();
      currentFields = [...currentFields, ...periodFields];
    }

    if (activeTab === 11 && totalSamplingPoints) {
      const samplingpointFields = Array.from(
        { length: totalSamplingPoints },
        (_, i) => `samplingpoints.${i}`,
      );
      currentFields = [...currentFields, ...samplingpointFields];
    }

    if (activeTab === 8) {
      if (!testDrugRequired) {
        currentFields = currentFields.filter(
          (fieldName) => !testDrugFieldNames.includes(fieldName),
        );
      }

      if (!refDrugRequired) {
        currentFields = currentFields.filter(
          (fieldName) => !refDrugFieldNames.includes(fieldName),
        );
      }
    }

    if (activeTab === 12) {
      const isValidVisit = await triggerVisit();
      if (!isValidVisit) return;
    }

    const isValid = await trigger(currentFields as any);

    if (!isValid) return;

    setActiveTab(targetTabId);
    setMaxUnlockedTab((prev) => Math.max(prev, targetTabId));
  };

  const onSubmit = async (formValues: StudyFormValues): Promise<void> => {
    const isValidVisit = await triggerVisit();
    if (!isValidVisit) return;

    const step1 = normalizeStudyForm(formValues);
    const checklistPayload = buildStudyChecklistsPayload(formValues, rawChecklistData);
    const crfPayload = buildStudyCRFsPayload(formValues);

    const visitValues = getVisitValues();
    const hasVisit = !!visitValues.visitname;

    const finalData: any = {
      ...normalizeFormValues(step1),
      ...checklistPayload,
      ...crfPayload,
      visits: hasVisit ? [{
        visitname: visitValues.visitname,
        visittype: Number(visitValues.visittype),
        visitcategory: Number(visitValues.visitcategory),
        samplingpoints: Array.isArray(visitValues.samplingpoints)
          ? visitValues.samplingpoints.filter(Boolean).map((sp: any) => String(sp).trim())
          : (visitValues.samplingpoints ? [String(visitValues.samplingpoints).trim()] : []),
        visitwindowdeviation: visitValues.deviation || "",
        visitdescription: visitValues.visitdescription || "",
      }] : []
    };

    delete finalData.configuredChecklistsData;
    delete finalData.configuredCRFsData;

    InsertMutation.mutate(finalData, {
      onSuccess: (response) => {
        showToastMessage("success", response?.message);
        queryClient.invalidateQueries({
          queryKey: ["study-selectAll"],
        });
        queryClient.invalidateQueries({
          queryKey: ["masterstudy-dropdown-selectAll"],
        });
        reset();
        onCreated?.();
        if (onClose) onClose();
        else navigate("/master/study", { state: { activeTab: "DRAFT" } });
      },
      onError: (error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const periodRows = Array.from({ length: numPeriods || 0 }, (_, i) => ({
    id: i + 1,
    period: i + 1,
  }));

  return (
    <div className={styles.fullScreen}>
      <style>{`
        .dropdown-menu-end {
          z-index: 2000 !important;
        }
      `}</style>
      <Container fluid className={styles.fullScreenContainer}>
        <Card className={styles.fullScreenCard}>
          <CardBody className={`${styles.fullScreenBody} p-0`}>
            <div className={styles.topBar}>
              <h5 className="mb-0">Add Study Details</h5>
              <Button
                type="button"
                color="link"
                className={styles.closeBtn}
                onClick={() => {
                  if (onClose) onClose();
                  else navigate("/master/study");
                }}
                aria-label="Close"
              >
                <i className="ri-close-line" />
              </Button>
            </div>
            <Row className={`g-0 ${styles.contentRow}`}>
              {/* Left Sidebar - Vertical Tabs */}
              <Col lg="3" className={`p-0 ${styles.tabSidebar}`}>
                <div
                  className={styles.progressLine}
                  style={
                    {
                      "--progress-height": `${progressHeight}px`,
                    } as React.CSSProperties
                  }
                ></div>
                <div className={styles.tabScrollArea} ref={sidebarRef}>
                  <div className={styles.tabWrapper}>
                    {tabConfig.map((tab) => {
                      const completed = isTabCompleted(tab.id);
                      const active = isTabActive(tab.id);
                      return (
                        <div
                          key={tab.id}
                          className={`${styles.tabButton} ${active ? styles.active : ""
                            } ${completed ? styles.completed : ""}`}
                          onClick={() => handleTabClick(tab.id)}
                          style={{
                            cursor:
                              tab.id > Math.max(maxUnlockedTab, activeTab) + 1
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              tab.id > Math.max(maxUnlockedTab, activeTab) + 1
                                ? 0.6
                                : 1,
                          }}
                          ref={(node) => {
                            tabItemRefs.current[tab.id] = node;
                          }}
                        >
                          <div className={styles.iconCircle}>
                            {completed ? (
                              <i className="ri-check-line"></i>
                            ) : (
                              <i className={tab.icon}></i>
                            )}
                          </div>
                          <span className={styles.tabName}>{tab.title}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Col>

              {/* Right Content Area */}
              <Col lg="9" className={styles.rightContent}>
                <SimpleBar style={{ height: "calc(100vh - 110px)" }}>
                  <div className={`p-4 ${styles.rightInner}`}>
                    <h5 className="mb-3">
                      {tabConfig.find((t) => t.id === activeTab)?.title}
                    </h5>
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className={styles.formLayout}
                    >
                      <div className={styles.formContent}>
                        {/* Always mounted to preserve state - hidden via CSS when not on tab 5 */}
                        <div style={{ display: activeTab === 5 ? "block" : "none" }}>
                          <ChecklistAssignmentStep
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            rawChecklistData={rawChecklistData}
                            checklistLoading={checklistMasterQuery.isLoading}
                          />
                        </div>
                        {/* Always mounted to preserve state - hidden via CSS when not on tab 6 */}
                        <div style={{ display: activeTab === 6 ? "block" : "none" }}>
                          <CRFAssignmentStep
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            studyCRFAssignmentQuery={studyCRFAssignmentQuery}
                          />
                        </div>
                        {activeTab === 5 || activeTab === 6 ? null : activeTab === 8 ? (
                          <>
                            <h6 className="text-primary mb-3">Test Drug</h6>
                            <CommonForm
                              key={testDrugRequired ? "testDrugRequired" : "testDrugOptional"}
                              fields={fields.filter((f) =>
                                testDrugFieldNames.includes(f.name as string),
                              ).map((field) => ({
                                ...field,
                                required: testDrugRequired,
                                rules: testDrugRequired ? field.rules : undefined,
                                optionsProps:
                                  field.name === "testexpirydate" && testmanufacturingdate
                                    ? { minDate: parseLocalDate(testmanufacturingdate) ?? undefined }
                                    : field.optionsProps,
                              }))}
                              control={control}
                              errors={errors}
                              columns={2}
                            />
                            <hr />
                            <h6 className="text-primary mb-3 mt-4">
                              Reference Drug
                            </h6>
                            <CommonForm
                              key={refDrugRequired ? "refDrugRequired" : "refDrugOptional"}
                              fields={fields.filter((f) =>
                                refDrugFieldNames.includes(f.name as string),
                              ).map((field) => ({
                                ...field,
                                required: field.name !== "refdrugname" && refDrugRequired,
                                rules: field.name !== "refdrugname" && refDrugRequired ? field.rules : undefined,
                                optionsProps:
                                  field.name === "refexpirydate" && refmanufacturingdate
                                    ? { minDate: parseLocalDate(refmanufacturingdate) ?? undefined }
                                    : field.optionsProps,
                              }))}
                              control={control}
                              errors={errors}
                              columns={2}
                            />
                          </>
                        ) : activeTab === 9 ? (
                          <>
                            <CommonForm
                              fields={[
                                ...fields.filter((f) =>
                                  [
                                    "screeningdate",
                                    "screeningvalidity",
                                  ].includes(f.name as string),
                                ),
                                ...fields.filter((f) =>
                                  ["washoutdays"].includes(f.name as string),
                                ),
                              ]}
                              control={control}
                              errors={errors}
                              columns={2}
                            />

                            <Row className="gy-3 form_input">
                              <Col md={6} className="mb-2 position-relative">
                                <Label className="mb-2 mt-2">
                                  Total Study Duration (auto)
                                </Label>
                                <Input
                                  bsSize="sm"
                                  type="text"
                                  value={totalStudyDurationText(
                                    watchedTimeline,
                                  )}
                                  placeholder="Auto calculated"
                                  disabled
                                />
                              </Col>
                            </Row>
                            <CommonTableIndexWise
                              columns={periodColumns(watch, control, errors, trigger)}
                              data={periodRows}
                              showStatus={false}
                              hideAction={true}
                              paginationShow={false}
                              pageSize={numPeriods || 1}
                              scrollable
                              maxHeight={170}
                              loading={false}
                              currentPage={1}
                              totalCount={periodRows.length}
                              onPageChange={() => { }}
                            />
                          </>
                        ) : activeTab === 11 ? (
                          <>
                            <>
                              <CommonForm
                                fields={fields.filter((f) =>
                                  [
                                    "biologicalmatrix",
                                    "totalsamplingpoints",
                                    "sampleprocessingmethod",
                                    "storagetemperature",
                                  ].includes(f.name as string),
                                )}
                                control={control}
                                errors={errors}
                                columns={2}
                              />
                              <Row className="mt-3">
                                {(samplingpoints || []).length > 0 && (
                                  <Col xs={12} className="mb-2">
                                    <div className="alert alert-info d-flex align-items-center py-2 px-3 small" role="alert">
                                      <i className="ri-information-line me-2 fs-16" />
                                      <div>
                                        <strong>Note:</strong> Enter timepoints in standard format (e.g., <code>-01.00</code>, <code>00.00</code>, <code>01.00</code>, <code>01.50</code>).
                                      </div>
                                    </div>
                                  </Col>
                                )}
                                {(samplingpoints || []).map((_, index) => (
                                  <Col
                                    key={index}
                                    xs="6"
                                    md="3"
                                    lg="2"
                                    className="mb-3"
                                  >
                                    <Label className="w-100 text-center fw-semibold">
                                      Timepoint {index + 1}{" "}
                                      <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                      name={`samplingpoints.${index}`}
                                      control={control}
                                      rules={{
                                        required: `Timepoint ${index + 1} is required`,
                                      }}
                                      render={({ field }) => (
                                        <Input
                                          {...field}
                                          type="text"
                                          placeholder="e.g. 01.00"
                                          className="text-center"
                                          invalid={!!samplingErrors?.[index]}
                                        />
                                      )}
                                    />
                                    {samplingErrors?.[index] && (
                                      <FormFeedback className="d-block text-center">
                                        {samplingErrors[index]?.message}
                                      </FormFeedback>
                                    )}
                                  </Col>
                                ))}
                              </Row>
                            </>
                          </>
                        ) : activeTab === 12 ? (
                          <>
                            <div>
                              <CommonForm
                                fields={ambulatoryFormFields}
                                control={visitControl}
                                errors={visitErrors}
                                columns={2}
                                setValue={setVisitValue}
                                getValues={getVisitValues}
                              />

                              <Row className="mt-3">
                                {(ambulatorySamplingPointsUI || []).length > 0 && (
                                  <Col xs={12} className="mb-2">
                                    <div className="alert alert-info d-flex align-items-center py-2 px-3 small" role="alert">
                                      <i className="ri-information-line me-2 fs-16" />
                                      <div>
                                        <strong>Note:</strong> Enter sampling points in standard format (e.g., <code>24</code>, <code>48</code>, <code>72</code>)
                                      </div>
                                    </div>
                                  </Col>
                                )}
                                {(ambulatorySamplingPointsUI || []).map((_, index) => (
                                  <Col key={index} xs="6" sm="4" md="2" className="mb-3">
                                    <Label className="w-100 text-center fw-semibold">
                                      Sampling Point {index + 1} <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                      name={`samplingpoints.${index}`}
                                      control={visitControl}
                                      rules={{
                                        required: `Sampling Point ${index + 1} is required`,
                                        validate: {
                                          pattern: (val) => {
                                            if (val !== "" && val !== undefined && val !== null) {
                                              if (!/^[0-9]+$/.test(String(val))) {
                                                return "Only numbers are allowed";
                                              }
                                            }
                                            return true;
                                          },
                                          greaterThan: (val) => {
                                            if (index > 0 && val !== "" && val !== undefined && val !== null) {
                                              const prevVal = getVisitValues(`samplingpoints.${index - 1}`);
                                              if (prevVal !== "" && prevVal !== undefined && !isNaN(Number(val)) && !isNaN(Number(prevVal))) {
                                                if (Number(val) <= Number(prevVal)) {
                                                  return `Must be greater than\nthe previous point`;
                                                }
                                              }
                                            }
                                            return true;
                                          }
                                        }
                                      }}
                                      render={({ field }) => (
                                        <Input
                                          {...field}
                                          type="text"
                                          placeholder="e.g. 24"
                                          className="text-center"
                                          invalid={!!(visitErrors?.samplingpoints as any)?.[index]}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            const val = e.target.value;
                                            if (val !== "" && val !== undefined) {
                                              triggerVisit(`samplingpoints.${index}` as any);
                                              if (getVisitValues(`samplingpoints.${index + 1}`)) {
                                                triggerVisit(`samplingpoints.${index + 1}` as any);
                                              }
                                            } else {
                                              clearVisitErrors(`samplingpoints.${index}` as any);
                                              if (getVisitValues(`samplingpoints.${index + 1}`)) {
                                                triggerVisit(`samplingpoints.${index + 1}` as any);
                                              }
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                    {(visitErrors?.samplingpoints as any)?.[index] && (
                                      <FormFeedback className="d-block text-center" style={{ position: "static", width: "100%", whiteSpace: "pre-line", fontSize: "11px", lineHeight: "1.2", textAlign: "center" }}>
                                        {(visitErrors?.samplingpoints as any)[index]?.message}
                                      </FormFeedback>
                                    )}
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          </>
                        ) : (
                          <CommonForm
                            fields={dynamicFields(agemin, bmimin, displaySequenceOptions, displayAliquoteSequenceOptions, activeTab)}
                            control={control}
                            errors={errors}
                            columns={2}
                            setValue={setValue}
                            getValues={getValues}
                          />
                        )}
                      </div>
                    </form>
                  </div>
                </SimpleBar>
                <div
                  className={`d-flex justify-content-between ${styles.formFooter}`}
                >
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={() => handleTabClick(Math.max(1, activeTab - 1))}
                    disabled={activeTab === 1}
                  >
                    Previous
                  </Button>
                  {activeTab < tabConfig.length ? (
                    <Button
                      type="button"
                      color="primary"
                      outline
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabClick(activeTab + 1);
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting || InsertMutation.isPending}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {InsertMutation.isPending && (
                        <Spinner size="sm" className="me-2" />
                      )}
                      Submit
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>

    </div>
  );
};

export default AddStudyForm;
